/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  ArrayInput,
  BooleanInput,
  required,
  maxLength,
  minLength,
  number,
  NumberInput,
  ReferenceInput,
  SelectInput,
  SimpleFormIterator,
  TextInput,
} from 'react-admin';
import {
  makeStyles,
  Grid,
  Paper,
  Typography,
  Tooltip,
} from '@material-ui/core';
import ImageUploadSelector from '../../ImageUploadSelector';
import ReferenceChild from '../../../components/ReferenceChild';
import { perPage } from '../../../helper';

const useStyles = makeStyles(() => ({
  paperBase: {
    padding: '40px 30px',
    margin: '15px 0',
  },
  w100: {
    width: '100%',
  },
  formRoot: {
    '& .MuiTextField-root': {
      width: '100%',
    },
    '& .MuiFilledInput-root': {
      backgroundColor: '#fdfdfd',
    },
  },
  pLR10: {
    padding: '0 10px',
  },
  center: {
    textAlign: 'center',
  },
  responsiveImage: {
    width: '100%',
    height: 'auto',
    maxWidth: '500px',
    cursor: 'zoom-in',
  },
}));

const twoColumnProps = {
  item: true,
  xs: 12,
  sm: 6,
};

const imageNameToUrl = {
  list:
    'https://zippityinfra.imgix.net/booking-app/1616609708180-service_preview_list.png',
  card: 'https://zippityinfra.imgix.net/booking-app/20220526-Detailing-Display.png',
  descriptionsModal:
    'https://zippityinfra.imgix.net/booking-app/1616609680900-service_preview_descriptions.png',
  moreDetailsModal:
    'https://zippityinfra.imgix.net/booking-app/20220526-Detailing-More-Information.png',
};

const validateName = [minLength(2), maxLength(255)];
const validateDisplayOrder = number();
const validateSubheader = [required(), minLength(2), maxLength(255)];
const validateText = [required(), minLength(2), maxLength(1023)];

const DisplayForm = ({
  record,
  handleImageClick,
  scheduleTypesWithCardView,
  selectedScheduleTypeIds,
  isCardView,
  setImageIsSaving,
  scheduleTypes,
  getDisplayRef,
  defaultDisplayOrder = 1,
}) => {
  const classes = useStyles();
  const [cardImageIsSaving, setCardImageIsSaving] = useState(false);
  const [inlineImageIsSaving, setInlineImageIsSaving] = useState(false);
  const [moreInfoImageIsSaving, setMoreInfoImageIsSaving] = useState(false);
  const displayRef = useRef();

  const isShowingListAndCardFields = useMemo(() => {
    if (!scheduleTypes.length || selectedScheduleTypeIds.length < 2)
      return false;
    let hasListView = false;
    let hasCardView = false;
    selectedScheduleTypeIds.forEach((id) => {
      const scheduleType = scheduleTypes.find((el) => el.id === id);
      if (scheduleTypesWithCardView.has(scheduleType?.name)) hasCardView = true;
      else hasListView = true;
    });
    return hasListView && hasCardView;
  }, [scheduleTypes, selectedScheduleTypeIds]);

  useEffect(() => {
    setImageIsSaving(
      cardImageIsSaving || inlineImageIsSaving || moreInfoImageIsSaving,
    );
  }, [cardImageIsSaving, inlineImageIsSaving, moreInfoImageIsSaving]);

  useEffect(() => {
    getDisplayRef(displayRef);
  }, []);

  const renderServiceDisplayDiagram = () => {
    const properties = isShowingListAndCardFields
      ? twoColumnProps
      : {
          item: true,
          xs: 12,
        };
    const listImage = (
      <Grid {...properties}>
        <img
          src={imageNameToUrl.list}
          alt="Labeled preview of service offering list"
          className={classes.responsiveImage}
          onClick={() => handleImageClick(imageNameToUrl.list)}
        />
      </Grid>
    );
    const cardImage = (
      <Grid {...properties}>
        <img
          src={imageNameToUrl.card}
          alt="Labeled preview of service offering card"
          className={classes.responsiveImage}
          onClick={() => handleImageClick(imageNameToUrl.card)}
        />
      </Grid>
    );
    const imagesToDisplay = [listImage];
    if (isShowingListAndCardFields) {
      imagesToDisplay.push(cardImage);
    } else if (isCardView) {
      imagesToDisplay[0] = cardImage;
    }
    return imagesToDisplay.map((image) => image);
  };

  const renderImageUploadInputs = () => {
    const inlineImageUploadInput = (
      <Grid item xs={12} className={classes.pLR10}>
        <ImageUploadSelector
          record={record}
          resource="service-offering"
          source="inline_image_url"
          folder="booking-app"
          setIsUploading={(val) => {
            setInlineImageIsSaving(val);
          }}
        />
      </Grid>
    );
    const cardImageUploadInput = (
      <Grid item xs={12} className={classes.pLR10}>
        <ImageUploadSelector
          record={record}
          resource="service-offering"
          source="card_image_url"
          folder="booking-app"
          setIsUploading={(val) => {
            setCardImageIsSaving(val);
          }}
        />
      </Grid>
    );
    const imageUploadInputs = [inlineImageUploadInput];
    if (isShowingListAndCardFields) {
      imageUploadInputs.push(cardImageUploadInput);
    } else if (isCardView) {
      imageUploadInputs[0] = cardImageUploadInput;
    }
    return imageUploadInputs.map((input) => input);
  };

  const renderSecondaryDisplaySection = () => {
    const descriptionsSection = (
      <>
        <h3>
          <strong>Descriptions</strong>
        </h3>
        <Grid container className={classes.center}>
          <Grid item xs={12}>
            <img
              src={imageNameToUrl.descriptionsModal}
              alt="Labeled preview of service offering descriptions modal"
              className={classes.responsiveImage}
              onClick={() => handleImageClick(imageNameToUrl.descriptionsModal)}
            />
          </Grid>
        </Grid>
        <Grid container className={classes.formRoot}>
          <Grid item xs={12} className={classes.pLR10}>
            <ArrayInput
              resource="service-offering-description"
              source="descriptions"
              margin="normal"
            >
              <SimpleFormIterator>
                <TextInput
                  resource="service-offering-description"
                  multiline
                  validate={validateSubheader}
                  source="subheader"
                />
                <TextInput
                  resource="service-offering-description"
                  multiline
                  validate={validateText}
                  source="text"
                />
              </SimpleFormIterator>
            </ArrayInput>
          </Grid>
        </Grid>
      </>
    );
    const moreInformationSection = (
      <>
        <h3>
          <strong>More Information</strong>
        </h3>
        <Grid container className={classes.center}>
          <Grid item xs={12}>
            <img
              src={imageNameToUrl.moreDetailsModal}
              alt="Labeled preview of service offering More Details modal"
              className={classes.responsiveImage}
              onClick={() => handleImageClick(imageNameToUrl.moreDetailsModal)}
            />
          </Grid>
        </Grid>
        <Grid container className={classes.formRoot}>
          <ArrayInput
            resource="service-offering"
            source="card_bullet_points"
            className={classes.w100}
            margin="normal"
          >
            <SimpleFormIterator>
              <TextInput
                resource="service-offering"
                multiline
                label="resources.service-offering.fields.bullet"
              />
            </SimpleFormIterator>
          </ArrayInput>
          <Grid {...twoColumnProps} className={classes.pLR10}>
            <TextInput
              resource="service-offering"
              multiline
              source="details_blurb"
            />
          </Grid>
          <Grid item xs={12} className={classes.pLR10}>
            <ImageUploadSelector
              record={record}
              resource="service-offering"
              source="more_info_image"
              folder="booking-app"
              setIsUploading={(val) => {
                setMoreInfoImageIsSaving(val);
              }}
            />
          </Grid>
        </Grid>
      </>
    );
    const sectionToDisplay = [descriptionsSection];
    if (isShowingListAndCardFields) {
      sectionToDisplay.push(
        <>
          <br />
          <hr className={classes.w100} />
          <br />
        </>,
      );
      sectionToDisplay.push(moreInformationSection);
    } else if (isCardView) {
      sectionToDisplay[0] = moreInformationSection;
    }
    return sectionToDisplay.map((section) => section);
  };

  return (
    <Paper className={classes.paperBase} ref={displayRef}>
      <Typography variant="h5" gutterBottom>
        Display
      </Typography>
      {scheduleTypes.length > 1 && !selectedScheduleTypeIds.length ? (
        <Grid container className={classes.center}>
          <Grid item xs={12}>
            <Typography gutterBottom>
              Please select a schedule type above
            </Typography>
          </Grid>
        </Grid>
      ) : (
        <>
          <Grid container className={classes.center}>
            {renderServiceDisplayDiagram()}
          </Grid>
          <Grid container className={classes.formRoot}>
            {(isShowingListAndCardFields || !isCardView) && (
              <>
                <Grid {...twoColumnProps} className={classes.pLR10}>
                  <TextInput
                    resource="service-offering"
                    multiline
                    validate={validateName}
                    source="long_name"
                  />
                </Grid>
                <Grid {...twoColumnProps} className={classes.pLR10}>
                  <ReferenceInput
                    resource="service-offering"
                    source="parent_service_offering_id"
                    reference="service-offering"
                    sort={{ field: 'short_name', order: 'ASC' }}
                    perPage={perPage}
                    allowEmpty
                  >
                    <ReferenceChild
                      optionText="short_name"
                      source="parent_service_offering_id"
                    />
                  </ReferenceInput>
                </Grid>
              </>
            )}
            <Grid {...twoColumnProps} className={classes.pLR10}>
              <NumberInput
                resource="service-offering"
                validate={validateDisplayOrder}
                source="display_order"
                defaultValue={defaultDisplayOrder}
              />
            </Grid>

            {(isShowingListAndCardFields || isCardView) && (
              <Grid {...twoColumnProps} className={classes.pLR10}>
                <TextInput
                  resource="service-offering"
                  multiline
                  source="subtitle"
                />
              </Grid>
            )}
            {renderImageUploadInputs()}
            {(isShowingListAndCardFields || !isCardView) && (
              <Tooltip
                title="Turns service name bold and red on the booking site"
                placement="left"
              >
                <Grid item xs={12} className={classes.pLR10}>
                  <BooleanInput
                    resource="service-offering"
                    source="display_emphasize"
                  />
                </Grid>
              </Tooltip>
            )}
          </Grid>
          <br />
          <hr className={classes.w100} />
          <br />
          {renderSecondaryDisplaySection()}
        </>
      )}
    </Paper>
  );
};

export default DisplayForm;
